import { transcriptionRetrySchema } from "@rosc/contracts";
import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

const fields = {
  version: z.literal(2),
  key: z.uuid(),
  body: transcriptionRetrySchema,
};
const schema = z.discriminatedUnion("kind", [
  z.strictObject({ ...fields, kind: z.literal("pending") }),
  z.strictObject({
    ...fields,
    kind: z.literal("accepted"),
    mode: z.enum(["audio", "notes"]),
  }),
]);
export type TranscriptionSubmission = z.output<typeof schema>;
const storageKey = (account: string, id: string) =>
  `rosc:transcription-submission:${encodeURIComponent(account)}:${id}`;
export const readTranscriptionSubmission = (
  account: string,
  id: string,
): TranscriptionSubmission | null => {
  assertNoObsoleteSubmission(account, "rosc:transcription-submission:");
  const value = sessionStorage.getItem(storageKey(account, id));
  if (value === null) return null;
  try {
    return schema.parse(JSON.parse(value));
  } catch {
    throw Error(
      "Saved transcription request cannot be read. Reselect this visit before abandoning recovery.",
    );
  }
};
export const abandonTranscriptionSubmission = (account: string, id: string) => {
  discardObsoleteSubmissions(account, "rosc:transcription-submission:");
  sessionStorage.removeItem(storageKey(account, id));
};
export const prepareTranscriptionSubmission = (
  account: string,
  id: string,
  body: unknown,
): TranscriptionSubmission => {
  const parsed = transcriptionRetrySchema.parse(body);
  const existing = readTranscriptionSubmission(account, id);
  if (existing?.kind === "pending") {
    if (existing.body.providerId !== parsed.providerId)
      throw Error(
        "Retry the saved provider or explicitly abandon this request first.",
      );
    return existing;
  }
  const value: TranscriptionSubmission = {
    version: 2,
    kind: "pending",
    key: crypto.randomUUID(),
    body: parsed,
  };
  sessionStorage.setItem(storageKey(account, id), JSON.stringify(value));
  return value;
};
export const completeTranscriptionSubmission = (
  account: string,
  id: string,
  pending: TranscriptionSubmission,
  mode: "audio" | "notes",
) => {
  if (readTranscriptionSubmission(account, id)?.key !== pending.key) return;
  sessionStorage.setItem(
    storageKey(account, id),
    JSON.stringify(
      schema.parse({ ...pending, version: 2, kind: "accepted", mode }),
    ),
  );
};
